// Setup walkthroughs surfaced as an info popup wherever a user enters
// credentials for a catalog item (Portal wizard, WorkflowBuilder node modal,
// ListenerWorkspace, SkillWorkspace). Keyed by the catalog item id.
//
// The name `LISTENER_SETUP_HELP` predates skills; it now covers both kinds.
// Add new entries here and they show up automatically in all four surfaces.

const LISTENER_KINDS = [
  'email_inbox', 'slack_messages', 'database_read',
  'webhook_incoming', 'scheduled_trigger', 'rss_feed', 'google_calendar',
  'twilio_messages',
].sort((a, b) => b.length - a.length);

export function listenerKindFromId(id) {
  if (!id) return '';
  const s = String(id);
  return LISTENER_KINDS.find((k) => s === k || s.startsWith(`${k}_`)) || '';
}

const CRED_NAME_KIND = {
  // Listeners
  SLACK_BOT_TOKEN: 'slack_messages',
  SLACK_CHANNEL_ID: 'slack_messages',
  IMAP_HOST: 'email_inbox',
  IMAP_EMAIL: 'email_inbox',
  IMAP_PASSWORD: 'email_inbox',
  RSS_FEED_URL: 'rss_feed',
  GOOGLE_CAL_ICS_URL: 'google_calendar',
  DB_QUERY: 'database_read',
  // Skills (credential-name sniffing is best-effort; catalog id is the source of truth)
  SMTP_HOST: 'email_manager',
  SMTP_PORT: 'email_manager',
  SMTP_EMAIL: 'email_manager',
  SMTP_PASSWORD: 'email_manager',
  SERP_API_KEY: 'web_search',
  ENDPOINT_URL: 'http_request',
  DATABASE_URL: 'database_query',
};

function kindFromCredNames(names) {
  for (const n of names || []) {
    const k = CRED_NAME_KIND[n];
    if (k) return k;
  }
  return '';
}

export function listenerKind(listener) {
  if (!listener) return '';
  if (listener.catalog_item_id) return String(listener.catalog_item_id);
  const byId = listenerKindFromId(listener.id);
  if (byId) return byId;
  const declared = Array.isArray(listener?.spec?.credentials)
    ? listener.spec.credentials.map((c) => (typeof c === 'string' ? c : c?.name)).filter(Boolean)
    : [];
  const userKeys = Object.keys(listener?.user_params || {});
  return kindFromCredNames([...declared, ...userKeys]);
}

export const LISTENER_SETUP_HELP = {
  // ---------- Listeners ----------
  email_inbox: {
    title: 'Set up the Email Inbox listener',
    body: (
      <>
        <p>
          This listener polls your mailbox over IMAP and emits one event per new email
          received after the agent was created. It reads with <code>BODY.PEEK</code>, so
          messages stay marked unread in your client.
        </p>
        <ol>
          <li>
            <strong>IMAP_HOST</strong> — your mail provider's IMAP server.
            <ul>
              <li>Gmail / Google Workspace: <code>imap.gmail.com</code></li>
              <li>Outlook / Microsoft 365: <code>outlook.office365.com</code></li>
              <li>iCloud: <code>imap.mail.me.com</code></li>
              <li>Yahoo: <code>imap.mail.yahoo.com</code></li>
            </ul>
          </li>
          <li><strong>IMAP_EMAIL</strong> — the full address (e.g. <code>you@gmail.com</code>).</li>
          <li>
            <strong>IMAP_PASSWORD</strong> — an <em>app password</em>, not your account password.
            With 2-factor auth on (which most providers now require for IMAP), regular passwords
            won't work.
            <ul>
              <li>Gmail: <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer">myaccount.google.com/apppasswords</a> → create a "Mail" password.</li>
              <li>Outlook: <a href="https://account.microsoft.com/security" target="_blank" rel="noopener noreferrer">account.microsoft.com/security</a> → Advanced security options → App passwords.</li>
              <li>iCloud: <a href="https://account.apple.com/account/manage" target="_blank" rel="noopener noreferrer">account.apple.com</a> → Sign-In and Security → App-Specific Passwords.</li>
            </ul>
          </li>
        </ol>
        <p>
          Choose <em>Unread only</em> mode for typical inbox monitoring, or
          {' '}<em>All recent</em> if you also want already-read messages forwarded.
        </p>
      </>
    ),
  },

  slack_messages: {
    title: 'Set up the Slack Messages listener',
    body: (
      <>
        <p>
          This listener polls a Slack channel for new messages using a bot token. You'll need
          two values: a <strong>Bot Token</strong> and a <strong>Channel ID</strong>.
        </p>
        <ol>
          <li>
            Go to <a href="https://api.slack.com/apps" target="_blank" rel="noopener noreferrer">api.slack.com/apps</a> →
            {' '}<em>Create New App</em> → <em>From scratch</em>, pick your workspace.
          </li>
          <li>
            Open <em>OAuth &amp; Permissions</em> → <em>Bot Token Scopes</em> and add:
            <ul>
              <li><code>channels:history</code> (or <code>groups:history</code> for private channels)</li>
              <li><code>channels:read</code></li>
              <li><code>users:info</code> — the listener calls <code>users.info</code> to resolve usernames</li>
            </ul>
          </li>
          <li>
            Click <em>Install to Workspace</em> at the top of the same page, then copy the
            {' '}<strong>Bot User OAuth Token</strong> (starts with <code>xoxb-</code>) into the
            {' '}<code>SLACK_BOT_TOKEN</code> field.
          </li>
          <li>
            In Slack, invite the bot into the channel you want to monitor:
            {' '}<code>/invite @YourBotName</code>. Without this, the API returns
            {' '}<code>not_in_channel</code>.
          </li>
          <li>
            Get the channel ID (not the name): in Slack, right-click the channel →
            {' '}<em>View channel details</em> → scroll to the bottom. It looks like
            {' '}<code>C0123456789</code>. Paste it into <code>SLACK_CHANNEL_ID</code>.
          </li>
        </ol>
        <p>
          The listener polls each tick and emits one event per new message since the agent was
          created. No webhook setup is needed.
        </p>
      </>
    ),
  },

  database_read: {
    title: 'Set up the Database Read listener',
    body: (
      <>
        <p>
          This listener runs a <code>SELECT</code> on every tick and emits one event per row.
          The runtime hashes each row to deduplicate, so the same row won't fire twice.
        </p>
        <ol>
          <li>
            <strong>DATABASE_URL</strong> — a PostgreSQL connection string in libpq format:
            {' '}<code>postgresql://user:password@host:5432/dbname</code>. URL-encode any
            special characters in the password (<code>@</code> → <code>%40</code>, etc.). For
            managed providers (Supabase, Neon, Heroku, RDS) copy the connection string from
            their dashboard.
          </li>
          <li>
            <strong>DB_QUERY</strong> — the SELECT you want to poll. Keep it cheap and bounded.
            Example:
            <br />
            <code>SELECT id, email, status FROM signups WHERE status = 'new' ORDER BY id DESC LIMIT 50</code>
            <br />
            Each returned row becomes one event. Order by a stable column (id, created_at) so
            rows have a deterministic identity for dedup.
          </li>
        </ol>
        <p>
          The listener needs <code>psycopg2</code> in the runtime; it ships with the default
          environment. For non-Postgres sources, fork the listener code and swap the driver.
        </p>
      </>
    ),
  },

  webhook_incoming: {
    title: 'Set up the Webhook listener',
    body: (
      <>
        <p>
          No credentials needed — this listener exposes an HTTPS endpoint. Any service that
          can POST JSON can trigger your agent.
        </p>
        <ol>
          <li>
            Copy the <strong>Webhook URL</strong> shown on the listener page (look for the
            {' '}<code>/webhook/&lt;agent-id&gt;/&lt;listener-id&gt;</code> URL).
          </li>
          <li>
            Paste it into your source system. For Zapier / Make use the
            {' '}<em>Webhooks → POST</em> action. For GitHub use <em>Repo Settings → Webhooks</em>.
            Stripe, Linear, Vercel etc. all have a webhook URL field in their dashboards.
          </li>
          <li>
            Send a test payload (most services have a "Send test" button). The POST body
            arrives in your listener as <code>event</code> — you can shape it however you
            want in <code>on_webhook(event)</code>.
          </li>
        </ol>
        <p>
          If your source signs requests (Stripe, GitHub, etc.), validate the signature inside
          {' '}<code>on_webhook</code> using whatever secret it shares. There's no built-in
          verification — the endpoint accepts any POST until you add a check.
        </p>
      </>
    ),
  },

  scheduled_trigger: {
    title: 'Set up the Scheduled Trigger',
    body: (
      <>
        <p>
          No credentials needed. The trigger fires on a cron expression you set in the
          {' '}<em>Schedule</em> tab — the runtime honors the schedule and only invokes
          your listener at the configured times.
        </p>
        <p>
          Cron format is the standard 5-field one: <code>minute hour day-of-month month day-of-week</code>.
        </p>
        <ul>
          <li><code>0 9 * * *</code> — every day at 9:00</li>
          <li><code>*/15 * * * *</code> — every 15 minutes</li>
          <li><code>0 9 * * 1-5</code> — 9 AM Monday–Friday</li>
          <li><code>0 0 1 * *</code> — midnight on the 1st of every month</li>
        </ul>
        <p>
          Pick a timezone in the same tab — cron expressions are evaluated in that zone.
          {' '}<a href="https://crontab.guru" target="_blank" rel="noopener noreferrer">crontab.guru</a>
          {' '}is the easiest way to sanity-check an expression.
        </p>
        <p>
          Customize <code>tick()</code> in <code>main.py</code> to emit a payload with the actual
          work for the agent — e.g. fetch data from an API and include it under <code>subject</code> /
          <code>body</code>, so the relevance classifier sees a real task and lets the run through.
        </p>
      </>
    ),
  },

  rss_feed: {
    title: 'Set up the RSS Feed listener',
    body: (
      <>
        <p>
          This listener fetches an RSS or Atom feed each tick and emits one event per new
          entry (deduplicated by link).
        </p>
        <ol>
          <li>
            <strong>RSS_FEED_URL</strong> — the full URL to the feed. Examples:
            <ul>
              <li>Hacker News front page: <code>https://hnrss.org/frontpage</code></li>
              <li>GitHub releases: <code>https://github.com/&lt;owner&gt;/&lt;repo&gt;/releases.atom</code></li>
              <li>Substack: <code>https://&lt;name&gt;.substack.com/feed</code></li>
              <li>Reddit subreddit: <code>https://www.reddit.com/r/&lt;sub&gt;/.rss</code></li>
            </ul>
          </li>
        </ol>
        <p>
          If the feed has many entries, the listener only picks up the first 10 per tick. Tune
          the poll interval to balance freshness vs. load — most feeds update every few
          minutes at most, so 5–15 minutes is usually plenty.
        </p>
      </>
    ),
  },

  google_calendar: {
    title: 'Set up the Google Calendar listener',
    body: (
      <>
        <p>
          Watches a Google Calendar via its <em>secret iCal URL</em>. Knowledge of the URL is
          the credential — no OAuth, no Cloud Console project.
        </p>
        <ol>
          <li>
            Open <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer">calendar.google.com</a> →
            click the gear → <em>Settings</em>.
          </li>
          <li>
            In the left sidebar under <em>Settings for my calendars</em>, click the calendar you
            want to watch.
          </li>
          <li>
            Scroll to <em>Integrate calendar</em> and copy <strong>Secret address in iCal
            format</strong> (it ends in <code>/basic.ics</code>). Paste that into
            {' '}<code>GOOGLE_CAL_ICS_URL</code>.
          </li>
        </ol>
        <p>
          Two caveats: Google caches the iCal feed for minutes-to-hours, so this is not
          real-time. And the URL is a bearer secret — if it leaks, regenerate it from the same
          settings page (which will break this listener until you re-paste).
        </p>
      </>
    ),
  },

  twilio_messages: {
    title: 'Set up the Twilio SMS listener',
    body: (
      <>
        <p>
          Receives inbound SMS by pointing Twilio's <em>A MESSAGE COMES IN</em> webhook at the
          listener's URL. The runtime verifies Twilio's <code>X-Twilio-Signature</code> using
          your <code>TWILIO_AUTH_TOKEN</code>, so <code>on_webhook</code> only runs on requests
          Twilio actually signed.
        </p>
        <ol>
          <li>
            Sign in at <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer">console.twilio.com</a>.
            On the account dashboard copy the <strong>Account SID</strong> (starts with
            {' '}<code>AC…</code>) into <code>TWILIO_ACCOUNT_SID</code> and the
            {' '}<strong>Auth Token</strong> into <code>TWILIO_AUTH_TOKEN</code>. The Auth Token
            is the exact secret Twilio uses to sign webhooks — a rotated token will make
            signature verification fail until you paste the new one here.
          </li>
          <li>
            Get a phone number: <em>Phone Numbers → Manage → Buy a number</em> (or use a trial
            number). Make sure it has <strong>SMS</strong> capability. Paste it in E.164
            format (e.g. <code>+14155551234</code>) into <code>TWILIO_PHONE_NUMBER</code>.
          </li>
          <li>
            Copy the <strong>Webhook URL</strong> shown on the listener page (the
            {' '}<code>/webhook/&lt;agent-id&gt;/&lt;listener-id&gt;</code> URL). In the Twilio
            console open your number → <em>Messaging Configuration</em> → set
            {' '}<em>A message comes in</em> to <strong>Webhook</strong>, method
            {' '}<strong>HTTP POST</strong>, and paste the URL. Save.
          </li>
          <li>
            Text your Twilio number from any phone. The listener emits one event per inbound
            message with <code>from</code>, <code>to</code>, <code>body</code>,
            {' '}<code>message_id</code>, and Twilio's <code>FromCity</code> / <code>FromState</code>
            {' '}/ <code>FromCountry</code> fields on the payload.
          </li>
        </ol>
        <p>
          Two gotchas: US A2P 10DLC registration is now required for production traffic to US
          numbers (do it under <em>Messaging → Regulatory Compliance</em>) — trial numbers
          work for testing without it. And Twilio signs the exact URL it POSTed to, so if your
          agent sits behind a proxy that rewrites the host or path, signature verification
          will fail — point Twilio directly at the public URL the runtime exposes.
        </p>
      </>
    ),
  },

  // ---------- Skills ----------
  email_manager: {
    title: 'Set up the Email Manager skill',
    body: (
      <>
        <p>
          The all-in-one email skill: send or draft mail over SMTP, and via IMAP archive / move /
          delete messages, mark them read or unread, list folders, and read attachments saved by
          the Email Inbox listener. Use the same provider you receive mail from so a single app
          password covers both protocols.
        </p>
        <ol>
          <li>
            <strong>SMTP_HOST</strong> and <strong>SMTP_PORT</strong> (required for send / draft):
            <ul>
              <li>Gmail / Google Workspace: <code>smtp.gmail.com</code>, port <code>587</code></li>
              <li>Outlook / Microsoft 365: <code>smtp.office365.com</code>, port <code>587</code></li>
              <li>iCloud: <code>smtp.mail.me.com</code>, port <code>587</code></li>
              <li>SendGrid: <code>smtp.sendgrid.net</code>, port <code>587</code> (user is <code>apikey</code>)</li>
            </ul>
          </li>
          <li><strong>SMTP_EMAIL</strong> — the From / login address (also reused as the IMAP user).</li>
          <li>
            <strong>SMTP_PASSWORD</strong> — an app password if the provider requires it (Gmail,
            Outlook, iCloud all do once 2FA is on). Same secret is used for IMAP login.
          </li>
          <li>
            <strong>IMAP_HOST</strong> (required for archive / move / delete / mark /
            list_folders / get_attachment; optional for <em>Draft</em> mode — drafts fall back
            to the agent's workspace without it).
          </li>
        </ol>
        <p>
          Tip: pick <em>Draft only</em> mode while you're testing — the agent will save outgoing
          messages for you to review instead of sending them. Pick <em>Read-only</em> if you want
          to triage existing mail without risking any changes.
        </p>
      </>
    ),
  },

  create_calendar_event: {
    title: 'Set up the Create Calendar Event skill',
    body: (
      <>
        <p>
          Adds events to your Google Calendar by emailing yourself a <code>.ics</code> invite —
          Gmail's invite auto-detect drops it on your calendar within about a minute. Uses the
          same SMTP credentials as the Send Email skill, so if you've already set that up there
          is nothing extra to enter.
        </p>
        <ol>
          <li>
            <strong>SMTP_HOST</strong> / <strong>SMTP_PORT</strong> — <code>smtp.gmail.com</code>,
            port <code>587</code>.
          </li>
          <li><strong>SMTP_EMAIL</strong> — your Gmail address (events are added to this
            account's primary calendar).</li>
          <li>
            <strong>SMTP_PASSWORD</strong> — a Gmail app password (with 2FA on, generate one at
            {' '}<a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer">
              myaccount.google.com/apppasswords
            </a>).
          </li>
        </ol>
        <p>
          One gotcha: Gmail's "Add invitations to my calendar" setting (in Calendar settings →
          Event settings) must be <em>From everyone</em> or <em>From known senders</em> for the
          invite to land automatically. The default is fine for most accounts.
        </p>
        <p>
          Limitation: this skill creates events but cannot edit or delete them afterwards. If
          you need full read/write, you'll want a real OAuth-backed Calendar API integration
          instead.
        </p>
      </>
    ),
  },

  read_calendar: {
    title: 'Set up the Read Calendar skill',
    body: (
      <>
        <p>
          Lets the agent answer questions about your schedule ("what do I have Tuesday?", "am I
          free at 3pm?") by fetching the same secret iCal URL the Google Calendar listener
          uses. If that listener is already set up, you can paste the same URL here.
        </p>
        <ol>
          <li>
            <strong>GOOGLE_CAL_ICS_URL</strong> — open <a href="https://calendar.google.com" target="_blank" rel="noopener noreferrer">calendar.google.com</a> →
            <em> Settings</em> → click your calendar in the sidebar → scroll to <em>Integrate
            calendar</em> → copy <strong>Secret address in iCal format</strong>.
          </li>
        </ol>
        <p>
          Same caveats as the listener: Google's iCal feed lags by minutes-to-hours, so very
          recent edits in Calendar may not show up immediately when the agent looks.
        </p>
      </>
    ),
  },

  slack_send: {
    title: 'Set up the Send Slack Message skill',
    body: (
      <>
        <p>
          Posts to Slack via <code>chat.postMessage</code>. Reuses the same bot token as the
          Slack Messages listener — if you've already set that one up, you can paste the
          same <code>xoxb-…</code> token here.
        </p>
        <ol>
          <li>
            If you don't have a bot yet, create one at
            {' '}<a href="https://api.slack.com/apps" target="_blank" rel="noopener noreferrer">api.slack.com/apps</a> →
            {' '}<em>Create New App</em> → <em>From scratch</em>.
          </li>
          <li>
            Add the <code>chat:write</code> scope under <em>OAuth &amp; Permissions →
            Bot Token Scopes</em>. Add <code>chat:write.public</code> too if you want to post
            to channels the bot hasn't been invited to.
          </li>
          <li>
            Click <em>Install to Workspace</em>, then copy the <strong>Bot User OAuth Token</strong>
            {' '}(<code>xoxb-…</code>) into <code>SLACK_BOT_TOKEN</code>.
          </li>
          <li>
            Invite the bot into any channel you want to post in: <code>/invite @YourBotName</code>.
            Without <code>chat:write.public</code> the bot must be a member.
          </li>
        </ol>
        <p>
          The skill takes a <code>channel</code> (id, not name) and <code>text</code>. Pick
          {' '}<em>Reply in thread</em> mode if you want the bot to keep replies inside the
          thread of the triggering message.
        </p>
      </>
    ),
  },

  send_sms: {
    title: 'Set up the Send SMS (Twilio) skill',
    body: (
      <>
        <p>
          Sends outbound SMS via Twilio's REST API. Reuses the same Account SID, Auth Token,
          and phone number as the <em>Twilio SMS</em> listener — if you've already set that
          one up, you can paste the same three values here.
        </p>
        <ol>
          <li>
            <strong>TWILIO_ACCOUNT_SID</strong> and <strong>TWILIO_AUTH_TOKEN</strong> — copy
            from the account dashboard at
            {' '}<a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer">console.twilio.com</a>.
            The SID starts with <code>AC…</code>.
          </li>
          <li>
            <strong>TWILIO_PHONE_NUMBER</strong> — an SMS-capable Twilio number in E.164
            format (e.g. <code>+14155551234</code>). Buy one under <em>Phone Numbers → Manage
            → Buy a number</em>, or use a trial number for testing. This is the default
            {' '}<em>From</em>; the agent can override it per-call by passing
            {' '}<code>from_number</code>.
          </li>
        </ol>
        <p>
          Trial accounts can only send to numbers you've verified under
          {' '}<em>Phone Numbers → Verified Caller IDs</em>, and every message is prefixed
          with a Twilio trial banner. Upgrade the account (add a payment method) to remove
          both limits.
        </p>
        <p>
          For US traffic, register a Messaging Service with A2P 10DLC under
          {' '}<em>Messaging → Regulatory Compliance</em> before going to production, or
          carriers will filter your messages. International destinations don't need this but
          may need a geo-permission enabled under <em>Messaging → Settings → Geo permissions</em>.
        </p>
      </>
    ),
  },

  database_query: {
    title: 'Set up the Database Query skill',
    body: (
      <>
        <p>
          Runs SQL against your database. In <em>Read only</em> mode anything that isn't a
          {' '}<code>SELECT</code> is rejected; in <em>Read &amp; write</em> mode
          {' '}<code>INSERT</code>/<code>UPDATE</code>/<code>DELETE</code> are allowed too.
        </p>
        <ol>
          <li>
            <strong>DATABASE_URL</strong> — a PostgreSQL connection string in libpq format:
            {' '}<code>postgresql://user:password@host:5432/dbname</code>. Copy it from your
            provider's dashboard (Supabase, Neon, Heroku, RDS, etc.).
          </li>
          <li>
            For production, point this at a <em>read-replica</em> or a role with
            {' '}<code>SELECT</code>-only grants when you can — the skill's read-mode guard is
            a string check, not a database-side enforcement.
          </li>
        </ol>
        <p>
          The skill needs <code>psycopg2</code> in the runtime; it ships with the default
          environment. Results are JSON-encoded (up to 100 rows per call).
        </p>
      </>
    ),
  },

  http_request: {
    title: 'Set up the HTTP Request skill',
    body: (
      <>
        <p>
          Fetches a URL with a chosen HTTP method. Two operating modes, picked automatically by
          whether you fill in <code>ENDPOINT_URL</code>:
        </p>
        <ul>
          <li>
            <strong>Pin a URL</strong> — set <code>ENDPOINT_URL</code> (e.g.
            {' '}<code>https://api.example.com/leads</code>). The agent can only choose the
            method and body; any <code>url</code> arg it tries to pass is ignored. The skill's
            tool description shown to the model includes the pinned URL, so the model knows
            exactly what it's targeting. Use this for production / single-endpoint integrations.
          </li>
          <li>
            <strong>Leave blank</strong> — the agent picks the URL per call. Useful for
            research-style agents that browse arbitrary APIs.
          </li>
        </ul>
        <p>
          No auth handling is built in. If the endpoint needs an API key or token, pass it
          inside the body or via a query string the agent constructs; for header-based auth
          you'll want to fork the skill code and inject the header in <code>forward()</code>.
        </p>
      </>
    ),
  },

  web_search: {
    title: 'Set up the Web Search skill',
    body: (
      <>
        <p>
          No credentials needed. The skill searches DuckDuckGo via the
          {' '}<a href="https://pypi.org/project/ddgs/" target="_blank" rel="noopener noreferrer">ddgs</a>
          {' '}library and returns the top results (title, link, snippet).
        </p>
        <ul>
          <li>
            Just drop the skill onto an agent and use it — there's nothing to configure.
          </li>
          <li>
            If you start seeing empty results or errors, DuckDuckGo is probably rate-limiting
            or has changed its HTML. Upgrade <code>ddgs</code> in <code>pyproject.toml</code>.
          </li>
          <li>
            For a different provider (Bing, Brave, You.com, SerpAPI) fork the skill code and
            swap the call inside <code>forward()</code> — the input/output shape can stay the same.
          </li>
        </ul>
      </>
    ),
  },

  file_read_write: {
    title: 'Set up the File Read/Write skill',
    body: (
      <>
        <p>
          No credentials needed. The skill reads and writes files inside the agent's
          sandboxed workspace (<code>workspace/</code> directory inside the agent's sandbox).
        </p>
        <ul>
          <li>
            Pick a <strong>mode</strong> on the Settings tab — <em>Read &amp; write</em>,
            {' '}<em>Read only</em>, or <em>Write only</em> — to restrict what the agent can do.
          </li>
          <li>
            Paths are relative to <code>workspace/</code> and can't escape it: writes to
            {' '}<code>../</code> or absolute paths are rejected at runtime.
          </li>
          <li>
            Files persist across agent runs and show up in the workspace file browser, so this
            is the right place for logs, CSV outputs, or anything the agent needs to remember
            without going through a real database.
          </li>
        </ul>
      </>
    ),
  },
};
